// Schedule / clear a follow-up on a lead. Opens from the lead drawer.
// Preset chips for the common "in N hours" / "tomorrow 9am" cases, plus
// a precise date + time picker. Source CRM uses the same shape.

import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import toast from "react-hot-toast";
import { Bell, Trash2 } from "lucide-react";
import { Button } from "../atoms";
import { useSetFollowUp, useClearFollowUp } from "../hooks/useFollowUps";
import FollowUpDateTime, { defaultFollowUpValue } from "./FollowUpDateTime";

dayjs.extend(relativeTime);

export default function FollowUpScheduler({ open, lead, onClose }) {
  const setFollowUp = useSetFollowUp();
  const clearFollowUp = useClearFollowUp();

  // A live lead always ends up with a date, so opening this on one that has
  // none pre-fills the default rather than making the user start from blank.
  const initial = useMemo(() => {
    if (lead?.followUp?.scheduledAt) return dayjs(lead.followUp.scheduledAt);
    const closed = lead?.status === "won" || lead?.status === "lost";
    return closed ? null : defaultFollowUpValue();
  }, [lead]);
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValue(initial);
      setError("");
    }
  }, [open, initial]);

  const handleSave = async () => {
    setError("");
    if (!value || !value.isValid()) {
      setError("Pick a date and time");
      return;
    }
    // Past dates are intentionally allowed — users log historical follow-ups
    // ("I called yesterday, plan to call again Friday") and the reminder
    // engine just won't fire for them.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      await setFollowUp.mutateAsync({
        leadId: lead._id,
        scheduledAt: value.toDate().toISOString(),
        scheduledTimezone: tz,
      });
      toast.success("Follow-up scheduled");
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to schedule");
    }
  };

  const handleClear = async () => {
    try {
      await clearFollowUp.mutateAsync({ leadId: lead._id });
      toast.success("Follow-up cleared");
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to clear");
    }
  };

  if (!lead) return null;

  const isPending = lead.followUp?.status === "pending";
  // Only a closed deal may be left with no next step, so Clear is offered only
  // there. On a live lead the server rejects it, so showing the button would
  // just be an error waiting to happen.
  const canClear = isPending && (lead.status === "won" || lead.status === "lost");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      footer={null}
      width={460}
      destroyOnClose
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600 }}>
          <Bell size={15} style={{ color: "var(--crm-primary)" }} />
          {isPending ? "Update follow-up" : "Schedule follow-up"}
        </div>
      }
      zIndex={1900}
      styles={{
        mask: { background: "rgba(15, 23, 42, 0.45)" },
        content: { borderRadius: 8, padding: 0 },
        header: { padding: "14px 20px", marginBottom: 0, borderBottom: "1px solid var(--color-border-subtle)" },
        body: { padding: "18px 20px" },
      }}
    >
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 16 }}>
        {lead.title}
        {lead.brandName ? ` · ${lead.brandName}` : ""}
      </div>

      <div style={{ marginBottom: 12 }}>
        <FollowUpDateTime
          value={value}
          onChange={(v) => { setValue(v); setError(""); }}
        />
      </div>

      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", minHeight: 18 }}>
        {value?.isValid() ? (
          <>
            {value.format("ddd, D MMM h:mm A")} · {value.fromNow()} · reminder window ±2h
          </>
        ) : (
          "Choose a preset or pick a date and time"
        )}
      </div>

      {error && (
        <div style={{ marginTop: 10, fontSize: 13, color: "var(--crm-danger)" }}>
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 20,
          paddingTop: 14,
          borderTop: "1px solid var(--color-border-subtle)",
        }}
      >
        {canClear ? (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 size={13} />}
            onClick={handleClear}
            disabled={clearFollowUp.isPending}
          >
            Clear
          </Button>
        ) : (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Required while the lead is open
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Button variant="secondary" size="md" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={!value?.isValid() || setFollowUp.isPending}
        >
          {setFollowUp.isPending ? "Saving…" : isPending ? "Update" : "Schedule"}
        </Button>
      </div>
    </Modal>
  );
}
